import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import {
    Button,
    CardMedia,
    Fab,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    TextField,
    MenuItem,
    Select,
    Typography
} from '@mui/material';

// project imports
import {
    gridSpacing,
    PAYMENT_MODE,
    SLOT_DURATION,
    IMAGE_FORMAT
} from 'store/constant';
import Avatar from 'ui-component/extended/Avatar';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';
import validationSchema from 'utils/schema/VenueSchema';

// assets
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DEFAULT_IMAGE from 'assets/images/default/image.png';

// third party
import { useFormik } from 'formik';

// styles
const ImageWrapper = styled('div')(({ theme }) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '4px',
    cursor: 'pointer',
    width: 55,
    height: 55,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.background.default,
    '& > svg': {
        verticalAlign: 'sub',
        marginRight: 6
    }
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default function VenueDetails({ actionType, venueUuid, venueDetails, setDetailsReload, handleChange, sportsTypes }) {

    const theme = useTheme();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [errorG, setErrorG] = useState('');
    const [previewImage, setPreviewImage] = useState(DEFAULT_IMAGE);
    const galleryImageRef = useRef([]);

    const handleBannerChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile.size > 5000000) { // 5mb
            setError('Image should be in 5mb.');
        } else {
            const typesplit = selectedFile.type.split('/');
            if (IMAGE_FORMAT.includes(typesplit[typesplit.length - 1])) {
                setError('');
                const formData = new FormData();
                formData.append('venueBanner', selectedFile);
                uploadFileApi.execute(formData);
            } else {
                setError('File format not supported');
            }
        }
        e.target.value = null;
        e.target.files = null;
    };

    const handleGalleryChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile.size > 5000000) { // 5mb
            setErrorG('Image should be in 5mb.');
        } else {
            const typesplit = selectedFile.type.split('/');
            if (IMAGE_FORMAT.includes(typesplit[typesplit.length - 1])) {
                setErrorG('');
                const formData = new FormData();
                formData.append('venueGallery', selectedFile);
                uploadFileApi.execute(formData);
            } else {
                setErrorG('File format not supported');
            }
        }
        e.target.value = null;
        e.target.files = null;
    };

    const removeGallery = (ind) => {
        let galleryImage = galleryImageRef.current;
        galleryImage.splice(ind, 1);
        galleryImageRef.current = galleryImage;
        formik.setFieldValue('gallery_image', galleryImageRef.current);
    };

    const updateVenueInfo = () => {
        const formikValues = { ...formik.values };
        formikValues.gallery_image = formikValues.gallery_image.join(',');
        formikValues.payment_mode = formikValues.payment_mode.join(',');
        formikValues.sports_type = formikValues.sports_type.join(',');
        if (actionType != 'create') {
            updateVenueApi.execute(formikValues, venueUuid);
        } else {
            formikValues.slot_duration = SLOT_DURATION[0].value;
            createVenueApi.execute(formikValues);
        }
    };

    const setFormValue = (data = {}) => {
        formik.setFieldValue('venue_name', data.venue_name ? data.venue_name : '');
        formik.setFieldValue('owner_name', data.owner_name ? data.owner_name : '');
        formik.setFieldValue('sports_type', data.sports_type ? data.sports_type.split(',') : []);
        formik.setFieldValue('address', data.address ? data.address : '');
        formik.setFieldValue('loc_city', data.loc_city ? data.loc_city : '');
        formik.setFieldValue('payment_mode', data.payment_mode ? data.payment_mode.split(',') : []);
        formik.setFieldValue('latitude', data.latitude ? data.latitude : '');
        formik.setFieldValue('longitude', data.longitude ? data.longitude : '');
        formik.setFieldValue('venue_map_link', data.venue_map_link ? data.venue_map_link : '');
        formik.setFieldValue('banner_image', data.banner_image ? data.banner_image : '');
        setPreviewImage(data.banner_image ? data.banner_image : DEFAULT_IMAGE);
        galleryImageRef.current = data.gallery_image ? data.gallery_image.split(',') : [];
        formik.setFieldValue('gallery_image', galleryImageRef.current);
    };

    const createVenueHandler = (response) => {
        navigate('/venue/edit/' + response.data.venueUuid, { replace: true });
        setDetailsReload(r => !r);
        handleChange(1);
    };

    const updateVenueHandler = () => {
        setDetailsReload(r => !r);
    };

    const uploadFileHandler = (response) => {
        let data = response.data;
        if (data.path === 'venue-banner') {
            formik.setFieldValue('banner_image', data.s3_path);
            setPreviewImage(data.s3_path);
        } else {
            let cpGalleryImage = galleryImageRef.current;
            cpGalleryImage.push(data.s3_path);
            galleryImageRef.current = cpGalleryImage;
            formik.setFieldValue('gallery_image', galleryImageRef.current);
        }
    };

    const uploadFileApi = useApi(VenueService.uploadFile, uploadFileHandler);
    const createVenueApi = useApi(VenueService.createVenue, createVenueHandler, true);
    const updateVenueApi = useApi(VenueService.updateVenueByUuid, updateVenueHandler, true);

    const formik = useFormik({
        initialValues: {
            venue_name: '',
            owner_name: '',
            sports_type: [],
            address: '',
            loc_city: '',
            payment_mode: [],
            latitude: '',
            longitude: '',
            venue_map_link: '',
            banner_image: '',
            gallery_image: []
        },
        validationSchema: validationSchema,
        onSubmit: updateVenueInfo
    });

    useEffect(() => {
        if (actionType != 'create' && venueDetails) {
            setFormValue(venueDetails);
        }
    }, [venueDetails]);

    return (
        <>
            <Grid container spacing={gridSpacing} mb={3} sx={{ justifyContent: 'center' }}>
                <Grid item xs={12} sm={5}></Grid>
                <Grid item xs={12} sm={3}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <label htmlFor="containedButtonFile">
                                <Avatar
                                    alt="Banner Image"
                                    src={previewImage}
                                    sx={{
                                        border: '2px #1b8a70 dotted',
                                        width: '120px',
                                        height: '120px',
                                        opacity: 1
                                    }}
                                />
                            </label>
                        </Grid>
                        {
                            error ? (
                                <Grid item xs={12}>
                                    <FormHelperText error id="standard-weight-helper-text-banner-venue">
                                        {error}
                                    </FormHelperText>
                                </Grid>
                            ) : formik.touched.banner_image && formik.errors.banner_image && (
                                <Grid item xs={12}>
                                    <FormHelperText error id="standard-weight-helper-text-banner_image-venue">
                                        {formik.errors.banner_image}
                                    </FormHelperText>
                                </Grid>
                            )

                        }
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                size="small"
                                component='label'
                                sx={{ marginLeft: '13px' }}
                            >
                                Upload Banner *
                                <VisuallyHiddenInput type='file' onChange={handleBannerChange} />
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4}></Grid>
            </Grid>
            <Grid container spacing={gridSpacing} mb={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="medium"
                        label="Venue Name*"
                        name="venue_name"
                        error={Boolean(formik.touched.venue_name && formik.errors.venue_name)}
                        value={formik.values.venue_name}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.venue_name && formik.errors.venue_name && (
                        <FormHelperText error id="standard-weight-helper-text-venuename-venue">
                            {formik.errors.venue_name}
                        </FormHelperText>
                    )}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="medium"
                        label="Owner Name*"
                        name="owner_name"
                        error={Boolean(formik.touched.owner_name && formik.errors.owner_name)}
                        value={formik.values.owner_name}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.owner_name && formik.errors.owner_name && (
                        <FormHelperText error id="standard-weight-helper-text-ownername-venue">
                            {formik.errors.owner_name}
                        </FormHelperText>
                    )}
                </Grid>
            </Grid>
            <Grid container spacing={gridSpacing} mb={3}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="medium">
                        <InputLabel id="sport-type-select-label">Sports Type*</InputLabel>
                        <Select
                            labelId="sport-type-select-label"
                            id="sport-type-select"
                            label="Sport Type*"
                            name="sports_type"
                            multiple
                            error={Boolean(formik.touched.sports_type && formik.errors.sports_type)}
                            value={formik.values.sports_type}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        >
                            {
                                sportsTypes.map((sportType, index) => {
                                    return (
                                        <MenuItem key={index} value={sportType.code}>{sportType.display_name}</MenuItem>
                                    )
                                })
                            }
                        </Select>
                    </FormControl>
                    {formik.touched.sports_type && formik.errors.sports_type && (
                        <FormHelperText error id="standard-weight-helper-text-sportstype-venue">
                            {formik.errors.sports_type}
                        </FormHelperText>
                    )}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="medium">
                        <InputLabel id="payment-mode-select-label">Payment Mode*</InputLabel>
                        <Select
                            labelId="payment-mode-select-label"
                            id="payment-mode-select"
                            label="Payment Mode*"
                            name="payment_mode"
                            multiple
                            error={Boolean(formik.touched.payment_mode && formik.errors.payment_mode)}
                            value={formik.values.payment_mode}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        >
                            {
                                PAYMENT_MODE.map((paymentMode, index) => {
                                    return (
                                        <MenuItem key={index} value={paymentMode.value}>{paymentMode.name}</MenuItem>
                                    )
                                })
                            }
                        </Select>
                    </FormControl>
                    {formik.touched.payment_mode && formik.errors.payment_mode && (
                        <FormHelperText error id="standard-weight-helper-text-payment-mode-venue">
                            {formik.errors.payment_mode}
                        </FormHelperText>
                    )}
                </Grid>
            </Grid>
            <Grid container spacing={gridSpacing} mb={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="medium"
                        label="Latitude*"
                        name="latitude"
                        error={Boolean(formik.touched.latitude && formik.errors.latitude)}
                        value={formik.values.latitude}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.latitude && formik.errors.latitude && (
                        <FormHelperText error id="standard-weight-helper-text-latitude-venue">
                            {formik.errors.latitude}
                        </FormHelperText>
                    )}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="medium"
                        label="Longitude*"
                        name="longitude"
                        error={Boolean(formik.touched.longitude && formik.errors.longitude)}
                        value={formik.values.longitude}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.longitude && formik.errors.longitude && (
                        <FormHelperText error id="standard-weight-helper-text-longitude-venue">
                            {formik.errors.longitude}
                        </FormHelperText>
                    )}
                </Grid>
            </Grid>
            <Grid container spacing={gridSpacing} mb={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        size="medium"
                        label="Address*"
                        name="address"
                        multiline
                        rows={4}
                        fullWidth
                        error={Boolean(formik.touched.address && formik.errors.address)}
                        value={formik.values.address}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.address && formik.errors.address && (
                        <FormHelperText error id="standard-weight-helper-text-address-venue">
                            {formik.errors.address}
                        </FormHelperText>
                    )}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        size="medium"
                        label="Venue Map URL*"
                        name="venue_map_link"
                        multiline
                        rows={4}
                        fullWidth
                        error={Boolean(formik.touched.venue_map_link && formik.errors.venue_map_link)}
                        value={formik.values.venue_map_link}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.venue_map_link && formik.errors.venue_map_link && (
                        <FormHelperText error id="standard-weight-helper-text-venue_map_link-venue">
                            {formik.errors.venue_map_link}
                        </FormHelperText>
                    )}
                </Grid>
            </Grid>
            <Grid container spacing={gridSpacing} mb={3}>
                <Grid item xs={12} sm={6}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" align="left">
                                Gallery Images*
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <div>
                                <TextField
                                    type="file"
                                    id="file-upload"
                                    fullWidth
                                    label="Gallery Image*"
                                    sx={{ display: 'none' }}
                                    onChange={handleGalleryChange}
                                />
                                <InputLabel
                                    htmlFor="file-upload"
                                    sx={{
                                        background: theme.palette.background.default,
                                        py: 3.75,
                                        px: 0,
                                        textAlign: 'center',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        mb: 3,
                                        '& > svg': {
                                            verticalAlign: 'sub',
                                            mr: 0.5
                                        }
                                    }}
                                >
                                    <CloudUploadIcon /> Drop image here to upload
                                </InputLabel>
                            </div>
                            {
                                galleryImageRef.current && galleryImageRef.current.length ?
                                    galleryImageRef.current.map((x, ind) =>
                                        <Grid container spacing={1} key={ind} sx={{ marginTop: '10px' }}>
                                            <Grid item>
                                                <ImageWrapper sx={{ width: '120px', height: '60px' }}>
                                                    <CardMedia
                                                        component="img"
                                                        image={x}
                                                        title="Gallery"
                                                    />
                                                </ImageWrapper>
                                            </Grid>
                                            <Grid item>
                                                <ImageWrapper>
                                                    <Fab color="secondary" size="small" onClick={() => removeGallery(ind)}>
                                                        <CloseIcon />
                                                    </Fab>
                                                </ImageWrapper>
                                            </Grid>
                                        </Grid>
                                    ) : (<></>)
                            }
                        </Grid>
                        {
                            errorG ? (
                                <Grid item xs={12}>
                                    <FormHelperText error id="standard-weight-helper-text-gallery-venue">
                                        {errorG}
                                    </FormHelperText>
                                </Grid>
                            ) : formik.touched.gallery_image && formik.errors.gallery_image && (
                                <Grid item xs={12}>
                                    <FormHelperText error id="standard-weight-helper-text-gallery_image-venue">
                                        {formik.errors.gallery_image}
                                    </FormHelperText>
                                </Grid>
                            )

                        }
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        size="medium"
                        label="City*"
                        name="loc_city"
                        fullWidth
                        error={Boolean(formik.touched.loc_city && formik.errors.loc_city)}
                        value={formik.values.loc_city}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.loc_city && formik.errors.loc_city && (
                        <FormHelperText error id="standard-weight-helper-text-loc_city-venue">
                            {formik.errors.loc_city}
                        </FormHelperText>
                    )}
                </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" mt={2}>
                <Grid item>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={formik.handleSubmit}
                    >
                        {actionType != 'create' ? 'Update' : 'Create'} Venue
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};